import { AmbientLight, PerspectiveCamera, PointLight, Scene, Vector3 } from 'three/webgpu';
import { type AudioFrame } from '../core/audio-frame.js';
import { BreathingPulse } from '../core/pulse.js';
import { createRandom } from '../core/random.js';
import { Spring } from '../core/spring.js';
import { type Bounds } from '../morph/baker.js';
import { type CameraMove } from '../morph/director.js';

const CAMERA_FOV = 52;
const POSITION_RESPONSE_S = 1.35;
const AIM_RESPONSE_S = 1.55;
const FOV_RESPONSE_S = 1.2;

/** What the stage needs to know about the thing it is filming. */
export interface Framing {
  readonly focus: Bounds;
  readonly move: CameraMove;
  /** Distance multiplier the shape asks for. */
  readonly framing: number;
  /** Increments once per arrival, so the stage knows a new shot has begun. */
  readonly visit: number;
  /** 0 while holding, up to 1 mid-morph. */
  readonly blending: number;
}

interface ShotInput {
  readonly focus: Bounds;
  readonly distance: number;
  /** Seconds since this shot began. */
  readonly shot: number;
  /** Seconds since the app started. */
  readonly clock: number;
  readonly angleOffset: number;
  readonly energy: number;
}

type MoveHandler = (input: ShotInput, lift: number, outPos: Vector3, outAim: Vector3) => void;

const MOVE_HANDLERS: Record<CameraMove, MoveHandler> = {
  orbit: (input, lift, outPos, outAim) => {
    const a = input.clock * (0.07 + input.energy * 0.05) + input.angleOffset;
    outPos.set(
      Math.cos(a) * input.distance,
      input.focus.y + lift * 0.35 + Math.sin(input.clock * 0.12) * lift * 0.1,
      Math.sin(a) * input.distance,
    );
    outAim.set(input.focus.x, input.focus.y, input.focus.z);
  },

  push: (input, lift, outPos, outAim) => {
    const a = input.clock * 0.04 + input.angleOffset;
    const breath = 0.65 + 0.35 * (0.5 + 0.5 * Math.cos(input.shot * 0.28));
    outPos.set(
      Math.cos(a) * input.distance * breath,
      input.focus.y + lift * (0.15 + 0.1 * Math.sin(input.shot * 0.2)),
      Math.sin(a) * input.distance * breath,
    );
    outAim.set(input.focus.x, input.focus.y + lift * 0.05, input.focus.z);
  },

  crane: (input, lift, outPos, outAim) => {
    const a = input.clock * 0.035 + input.angleOffset;
    const rise = 0.5 - 0.5 * Math.cos(Math.min(Math.PI, input.shot * 0.22));
    outPos.set(
      Math.cos(a) * input.distance * 0.94,
      input.focus.y - lift * 0.45 + rise * lift * 1.85,
      Math.sin(a) * input.distance * 0.94,
    );
    outAim.set(input.focus.x, input.focus.y + lift * (0.1 - rise * 0.2), input.focus.z);
  },

  glide: (input, lift, outPos, outAim) => {
    const x = Math.sin(input.shot * 0.16 + input.angleOffset) * input.distance * 1.15;
    const z = Math.cos(input.shot * 0.12 + input.angleOffset) * input.distance * 0.9;
    outPos.set(x, input.focus.y + lift * (0.2 + 0.1 * Math.sin(input.shot * 0.25)), z);
    outAim.set(input.focus.x, input.focus.y, input.focus.z);
  },

  sweep: (input, lift, outPos, outAim) => {
    const a = input.clock * (0.13 + input.energy * 0.08) + input.shot * 0.12 + input.angleOffset;
    outPos.set(
      Math.cos(a) * input.distance * 0.88,
      input.focus.y + lift * (0.15 + 0.25 * Math.sin(a * 1.5)),
      Math.sin(a) * input.distance * 0.88,
    );
    outAim.set(input.focus.x, input.focus.y, input.focus.z);
  },

  hover: (input, lift, outPos, outAim) => {
    const a = input.clock * 0.02 + input.angleOffset;
    const bobY = Math.sin(input.clock * 0.15) * lift * 0.16;
    const bobR = 1 + 0.08 * Math.cos(input.clock * 0.11);
    outPos.set(
      Math.cos(a) * input.distance * 0.88 * bobR,
      input.focus.y + lift * 0.38 + bobY,
      Math.sin(a) * input.distance * 0.88 * bobR,
    );
    outAim.set(input.focus.x, input.focus.y, input.focus.z);
  },

  overhead: (input, lift, outPos, outAim) => {
    const a = input.clock * 0.06 + input.angleOffset;
    const radius = input.distance * 0.42;
    outPos.set(
      Math.cos(a) * radius,
      input.focus.y + lift * 1.85 + Math.sin(input.clock * 0.18) * lift * 0.15,
      Math.sin(a) * radius,
    );
    outAim.set(input.focus.x, input.focus.y - lift * 0.2, input.focus.z);
  },

  lowAngle: (input, lift, outPos, outAim) => {
    const a = input.clock * 0.05 + input.angleOffset;
    const radius = input.distance * 0.68;
    outPos.set(
      Math.cos(a) * radius,
      input.focus.y - lift * 0.95 + Math.sin(input.clock * 0.14) * lift * 0.12,
      Math.sin(a) * radius,
    );
    outAim.set(input.focus.x, input.focus.y + lift * 0.4, input.focus.z);
  },

  closeUp: (input, lift, outPos, outAim) => {
    const a = input.clock * (0.08 + input.energy * 0.06) + input.angleOffset;
    const radius = input.distance * 0.52 * (1 + 0.1 * Math.sin(input.shot * 0.3));
    outPos.set(
      Math.cos(a) * radius,
      input.focus.y + lift * 0.12 + Math.sin(input.shot * 0.2) * lift * 0.1,
      Math.sin(a) * radius,
    );
    outAim.set(
      input.focus.x + Math.sin(input.shot * 0.2) * lift * 0.15,
      input.focus.y + lift * 0.1,
      input.focus.z,
    );
  },

  corkscrew: (input, lift, outPos, outAim) => {
    const a = input.shot * 0.32 + input.angleOffset;
    const height = (0.5 - 0.5 * Math.cos(input.shot * 0.26)) * lift * 2 - lift * 0.5;
    const radius = input.distance * (0.72 + 0.2 * Math.sin(input.shot * 0.28));
    outPos.set(Math.cos(a) * radius, input.focus.y + height, Math.sin(a) * radius);
    outAim.set(input.focus.x, input.focus.y + lift * 0.1, input.focus.z);
  },

  dutchOrbit: (input, lift, outPos, outAim) => {
    const a = input.clock * (0.09 + input.energy * 0.07) + input.angleOffset;
    outPos.set(
      Math.cos(a) * input.distance * 0.85,
      input.focus.y + lift * 0.28 + Math.sin(input.clock * 0.2) * lift * 0.15,
      Math.sin(a) * input.distance * 0.85,
    );
    outAim.set(input.focus.x, input.focus.y, input.focus.z);
  },
};

const ALL_MOVES: readonly CameraMove[] = [
  'orbit',
  'push',
  'crane',
  'glide',
  'sweep',
  'hover',
  'overhead',
  'lowAngle',
  'closeUp',
  'corkscrew',
  'dutchOrbit',
];

function rollFor(move: CameraMove, clock: number, shot: number, frame: AudioFrame): number {
  if (move === 'dutchOrbit') {
    return Math.sin(clock * 0.25) * 0.22 + 0.14;
  }
  if (move === 'corkscrew') {
    return Math.sin(shot * 0.32) * 0.16;
  }
  return Math.sin(clock * 0.035) * 0.05 + (frame.treble - frame.bass) * 0.03;
}

/**
 * Scene container and dynamic camera director. The camera rides critically-damped
 * springs towards dynamic multi-angle shots, frequently changing perspectives
 * with the rhythm, beats, and energy of the music.
 */
export class Stage {
  public readonly scene = new Scene();
  public readonly camera: PerspectiveCamera;
  private readonly position = [
    new Spring(POSITION_RESPONSE_S),
    new Spring(POSITION_RESPONSE_S),
    new Spring(POSITION_RESPONSE_S),
  ] as const;
  private readonly aim = [
    new Spring(AIM_RESPONSE_S),
    new Spring(AIM_RESPONSE_S),
    new Spring(AIM_RESPONSE_S),
  ] as const;
  private readonly fovSpring = new Spring(FOV_RESPONSE_S, CAMERA_FOV);
  private readonly kick = new Spring(0.85);
  private readonly beatPulse = new BreathingPulse(0.08, 0.45);
  private readonly random = createRandom(0x4a_7e_2b);
  private readonly shotTarget = new Vector3();
  private readonly aimTarget = new Vector3();
  private currentMove: CameraMove = 'orbit';
  private shotAngleOffset = 0;
  private shotDuration = 5.5;
  private energyAccumulator = 0;
  private shotTime = 0;
  private lastVisit = -1;

  constructor(aspect: number) {
    this.camera = new PerspectiveCamera(CAMERA_FOV, aspect, 0.1, 600);
    this.camera.position.set(0, 4, 26);

    const ambient = new AmbientLight(0x0a_0a_2a, 0.6);
    const blueSide = new PointLight(0x22_44_ff, 60, 0, 1.8);
    blueSide.position.set(-4, 3.5, 3);
    const redSide = new PointLight(0xff_22_44, 60, 0, 1.8);
    redSide.position.set(4, 2.5, 3);
    this.scene.add(ambient, blueSide, redSide);
  }

  public update(frame: AudioFrame, clock: number, deltaSeconds: number, framing: Framing): void {
    this.stepShot(frame, deltaSeconds, framing);

    const { focus } = framing;
    const distance = focus.radius * framing.framing * (1 + framing.blending * 0.14) + 4;
    const input: ShotInput = {
      focus,
      distance,
      shot: this.shotTime,
      clock,
      angleOffset: this.shotAngleOffset,
      energy: frame.level + frame.bass * 0.5,
    };

    const handler = MOVE_HANDLERS[this.currentMove];
    handler(input, focus.radius, this.shotTarget, this.aimTarget);
    this.shotTarget.x += focus.x;
    this.shotTarget.z += focus.z;

    // A beat nudges the camera along its own view axis — a gentle hydrostatic swell.
    const pulse = this.beatPulse.update(frame, deltaSeconds);
    const push = this.kick.update(pulse, deltaSeconds) * focus.radius * 0.045;
    this.aimTarget.x += Math.sin(clock * 0.043) * focus.radius * 0.08;
    this.aimTarget.y += Math.sin(clock * 0.031) * focus.radius * 0.06;
    this.shotTarget.lerp(this.aimTarget, push / Math.max(1, distance));

    // Dynamic FOV breathing with bass hits (subtle focal depth breathing)
    const targetFov = CAMERA_FOV - frame.bass * 3.2 + frame.level * 1.6;
    this.camera.fov = this.fovSpring.update(targetFov, deltaSeconds);
    this.camera.updateProjectionMatrix();

    this.camera.position.set(
      this.position[0].update(this.shotTarget.x, deltaSeconds),
      this.position[1].update(this.shotTarget.y, deltaSeconds),
      this.position[2].update(this.shotTarget.z, deltaSeconds),
    );

    const roll = rollFor(this.currentMove, clock, this.shotTime, frame);
    this.camera.up.set(Math.sin(roll), Math.cos(roll), 0);
    this.camera.lookAt(
      this.aim[0].update(this.aimTarget.x, deltaSeconds),
      this.aim[1].update(this.aimTarget.y, deltaSeconds),
      this.aim[2].update(this.aimTarget.z, deltaSeconds),
    );
  }

  public resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  private stepShot(frame: AudioFrame, deltaSeconds: number, framing: Framing): void {
    this.shotTime += deltaSeconds;
    this.energyAccumulator += (frame.beatIntensity + frame.bass) * deltaSeconds;

    const isNewVisit = framing.visit !== this.lastVisit;
    const isPhraseBreak = this.shotTime >= 3.6 && frame.beat && frame.beatIntensity > 0.45;
    const isEnergySurge = this.shotTime >= 3.4 && this.energyAccumulator > 2.8;
    const isTimeout = this.shotTime >= this.shotDuration;

    if (isNewVisit || isPhraseBreak || isEnergySurge || isTimeout) {
      this.triggerShotChange(framing);
    }
  }

  private triggerShotChange(framing: Framing): void {
    if (framing.visit === this.lastVisit) {
      const candidates = ALL_MOVES.filter((m) => m !== this.currentMove);
      const pick = Math.floor(this.random() * candidates.length);
      this.currentMove = candidates[pick] ?? 'orbit';
    } else {
      this.lastVisit = framing.visit;
      this.currentMove = framing.move;
    }
    this.shotAngleOffset =
      (this.shotAngleOffset + Math.PI * (0.55 + this.random() * 0.9)) % (Math.PI * 2);
    this.shotTime = 0;
    this.energyAccumulator = 0;
    this.shotDuration = 4.2 + this.random() * 3.2;
  }
}
